import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Safely format a price value to a string with 2 decimal places.
 * Handles number, string, Decimal-like objects, null, and undefined.
 * Returns "0.00" for invalid/missing values.
 * 
 * @param value - The price value (can be number, string, Decimal, null, undefined)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string like "123.45"
 */
export function formatMoney(value: unknown, decimals: number = 2): string {
  // Handle null/undefined
  if (value == null) {
    return '0.00';
  }

  // Handle number
  if (typeof value === 'number') {
    if (isNaN(value) || !isFinite(value)) {
      return '0.00';
    }
    return value.toFixed(decimals);
  }

  // Handle string
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || !isFinite(parsed)) {
      return '0.00';
    }
    return parsed.toFixed(decimals);
  }

  // Handle Decimal-like object (has toFixed method)
  if (typeof value === 'object' && value !== null && 'toFixed' in value && typeof (value as any).toFixed === 'function') {
    try {
      return (value as any).toFixed(decimals);
    } catch {
      return '0.00';
    }
  }

  // Handle Decimal-like object (has toString method that returns a number string)
  if (typeof value === 'object' && value !== null && 'toString' in value) {
    try {
      const str = (value as any).toString();
      const parsed = parseFloat(str);
      if (isNaN(parsed) || !isFinite(parsed)) {
        return '0.00';
      }
      return parsed.toFixed(decimals);
    } catch {
      return '0.00';
    }
  }

  // Fallback for any other type
  return '0.00';
}

/**
 * Safely extract a string message from an API error for display in toasts.
 *
 * NestJS ValidationPipe returns `message` as an array (sometimes an array of
 * objects). Passing that directly to toast.error() makes Sonner store a
 * non-string as the toast message; its internal dismiss setTimeout then fires,
 * React re-renders <Toaster>, and throws "Objects are not valid as a React
 * child" — MADDOX_FRONTEND-8.
 */
export function toErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback;
  const e = error as Record<string, unknown>;
  const data = (e.response as Record<string, unknown> | undefined)?.data;
  const raw = (data as Record<string, unknown> | undefined)?.message ?? e.message;
  if (typeof raw === 'string' && raw) return raw;
  if (Array.isArray(raw)) {
    const joined = raw.filter((s): s is string => typeof s === 'string').join(', ');
    return joined || fallback;
  }
  return fallback;
}
